import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../../api";

const StickyNavSection = () => {
  const { miniSlug, slug, collegeSlug, programSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const activeSlug = slug || collegeSlug;
  const isProgramDetailsPage = !!programSlug;

  const [menuItems, setMenuItems] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  const underlineRef = useRef(null);
  const tabsRef = useRef(null);

  // ⭐ IMPORTANT FLAG
  const isStickyClick = useRef(false);

  /* ------------------------------------
     FETCH MENU SECTIONS
  ------------------------------------ */
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/api/${miniSlug}/${activeSlug}`,
        );
        const sections = res.data.sections || {};

        const defaultMenu = [
          { id: "overview", label: "Overview", objectKey: "overview" },
          { id: "programs", label: "Programs", objectKey: "programs" },
          {
            id: "principal",
            label: sections?.principal?.designation
              ? `${sections.principal.designation} Speak`
              : "Principal Speak",
            objectKey: "principal",
          },
          { id: "facilities", label: "Facilities", objectKey: "facilities" },
          {
            id: "faculty-list-link",
            label: "Faculty List",
            objectKey: "faculty_list",
            type: "link",
          },
          {
            id: "student-life",
            label: "Student Life",
            objectKey: "student_life",
          },
          {
            id: "news-events",
            label: "News & Events",
            objectKey: "news_events",
          },
          { id: "more", label: "Find Out More", objectKey: "find_out_more" },
        ];

        const validMenu = defaultMenu.filter((item) => {
          const data = sections[item.objectKey];
          if (!data) return false;

          if (item.objectKey === "faculty_list") {
            return Array.isArray(data.groups) && data.groups.length > 0;
          }

          return Object.keys(data).length > 0;
        });

        setMenuItems(validMenu);
      } catch (err) {
        console.error(err);
      }
    };

    fetchSections();
  }, [miniSlug, activeSlug]);

  /* ------------------------------------
     PROGRAM DETAILS PAGE FIX
  ------------------------------------ */
  useEffect(() => {
    if (isProgramDetailsPage) {
      setActiveTab("programs");
    }
  }, [isProgramDetailsPage]);

  /* ------------------------------------
     MOVE UNDERLINE
  ------------------------------------ */
  const moveUnderline = (el) => {
    if (!el || !underlineRef.current) return;

    const rect = el.getBoundingClientRect();
    const parentLeft = el.parentElement.getBoundingClientRect().left;

    underlineRef.current.style.left = `${rect.left - parentLeft}px`;
    underlineRef.current.style.width = `${rect.width}px`;
  };

  /* ------------------------------------
     TAB CLICK HANDLER
  ------------------------------------ */
  const handleTabClick = (item, e) => {
    e.preventDefault();
    isStickyClick.current = true;

    if (item.id === "faculty-list-link") {
      navigate(`/${miniSlug}/${activeSlug}/faculty-list`);
      return;
    }

    if (isProgramDetailsPage) {
      navigate(`/${miniSlug}/${activeSlug}#${item.id}`);
      return;
    }

    setActiveTab(item.id);

    const el = document.getElementById(item.id);
    if (el) {
      const offset = window.innerWidth <= 768 ? -140 : -100;
      const y = el.getBoundingClientRect().top + window.pageYOffset + offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

    moveUnderline(e.currentTarget);

    setTimeout(() => {
      isStickyClick.current = false;
    }, 800);
  };

  /* ------------------------------------
     SCROLL LISTENER (PROTECTED)
  ------------------------------------ */
  useEffect(() => {
    if (isProgramDetailsPage) return;

    const handleScroll = () => {
      if (isStickyClick.current) return;

      const offset = 150;
      let current = "";

      menuItems
        .filter((i) => i.type !== "link")
        .forEach((item) => {
          const section = document.getElementById(item.id);
          if (!section) return;

          if (
            window.scrollY >= section.offsetTop - offset &&
            window.scrollY < section.offsetTop + section.offsetHeight - offset
          ) {
            current = item.id;
          }
        });

      if (current && current !== activeTab) {
        setActiveTab(current);
        const el = tabsRef.current?.querySelector(
          `[data-target="#${current}"]`,
        );
        if (el) moveUnderline(el);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menuItems, activeTab, isProgramDetailsPage]);

  /* ------------------------------------
     INITIAL UNDERLINE
  ------------------------------------ */
  useEffect(() => {
    const el = tabsRef.current?.querySelector(".active");
    if (el) moveUnderline(el);
  }, [menuItems, activeTab]);

  const handleKeyDown = (e, index, item) => {
    const tabs =
      tabsRef.current?.querySelectorAll('[role="tab"]') || [];

    let nextIndex = index;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        nextIndex = (index + 1) % tabs.length;
        tabs[nextIndex].focus();
        break;

      case "ArrowLeft":
        e.preventDefault();
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
        break;

      case "Home":
        e.preventDefault();
        tabs[0].focus();
        break;

      case "End":
        e.preventDefault();
        tabs[tabs.length - 1].focus();
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        handleTabClick(item, e);
        break;

      default:
        break;
    }
  };

  if (!menuItems.length) return null;

  return (
    <section className="navigation_section sticky-nav">
      <div className="container p-2 px-md-5">
        <div className="row align-items-center tab-header">
          <div className="col-2 col-md-1">
            <img
              src="/assets/images/chettinad-logo-single.png"
              alt="logo"
              className="img-fluid"
              style={{ width: "40%" }}
            />
          </div>

          <div className="col-10 col-md-11">
            <div className="tab-menu py-1" ref={tabsRef}>
              <ul role="tablist" aria-label="College Information Tabs">
                {menuItems.map((item, index) => {
                  const isActive = activeTab === item.id;

                  return (
                    <li
                      key={item.id}
                      id={`tab-${item.id}`}
                      role="tab"
                      tabIndex={isActive ? 0 : -1}
                      aria-selected={isActive}
                      aria-controls={`panel-${item.id}`}
                      className={isActive ? "active" : ""}
                      data-target={`#${item.id}`}
                      onClick={(e) => handleTabClick(item, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, item)}
                    >
                      {item.label}
                    </li>
                  );
                })}

                <div className="underline" ref={underlineRef}></div>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StickyNavSection;
